/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  buildAnalyticsParam,
  buildSsoLabel,
  CATEGORY,
  EVENT_NAMES,
  SCREEN_NAME,
} from '../config/analytics';
import type { SocialProvider } from '../managers/sso/models';

/** Consumer for in-app SSO login / signup widget analytics. */
export const SSO_ANALYTICS_CONSUMER = 'app';

/** Consumer used in the login / signup success events. */
export const SSO_AUTH_SUCCESS_CONSUMER = 'homepage';

/** Passwordless email method segment used in SSO login / signup labels. */
export const SSO_METHOD_EMAIL_ONLY = 'emailOnly';

/** @see SSO_METHOD_EMAIL_ONLY */
export const SSO_SIGNUP_METHOD_EMAIL_ONLY = SSO_METHOD_EMAIL_ONLY;

export const SSO_METHOD = {
  emailOnly: 'emailOnly',
  emailPassword: 'emailPassword',
  mobilePhoneLogin: 'mobilePhoneLogin',
} as const;

export type SsoMethod = (typeof SSO_METHOD)[keyof typeof SSO_METHOD];

/** Login / signup method used in the success event label. */
export type SsoAuthMethod = SsoMethod | SocialProvider | 'passkey';

/** Which success event `useFinishSignIn` logs. */
export type SsoAuthEvent = 'login' | 'signup' | 'none';

const PASSTHROUGH_AUTH_METHODS: readonly string[] = [
  'google',
  'facebook',
  'apple',
  'wechat',
  'passkey',
];

/**
 * Converts a login `method` to the label value. No method means
 * email + password.
 */
export function resolveSsoAuthMethod(method?: string): SsoAuthMethod {
  if (method && PASSTHROUGH_AUTH_METHODS.includes(method)) {
    return method as SsoAuthMethod;
  }
  if (method === 'mobile') return SSO_METHOD.mobilePhoneLogin;
  return SSO_METHOD.emailPassword;
}

const SSO_AUTH_METHOD_VALUES: ReadonlySet<string> = new Set([
  ...Object.values(SSO_METHOD),
  ...PASSTHROUGH_AUTH_METHODS,
]);

/**
 * Returns the `signupMethod` route param only if it is a known method,
 * otherwise undefined.
 */
export function parseSsoAuthMethodParam(
  value: string | string[] | undefined,
): SsoAuthMethod | undefined {
  return typeof value === 'string' && SSO_AUTH_METHOD_VALUES.has(value)
    ? (value as SsoAuthMethod)
    : undefined;
}

/** Email OTP verification method segment used in SSO OTP labels. */
export const SSO_VERIFICATION_EMAIL_OTP = 'emailOTP';

export const SSO_OTP_CHANNEL = {
  sms: 'smsOTP',
  whatsapp: 'whatsappOTP',
} as const;

export const SSO_OTP_ACTION = {
  requestOTP: 'requestOTP',
  resendSMS: 'resendSMS',
  resendWhatsapp: 'resendWhatsapp',
  updatePhoneNumber: 'updatePhoneNumber',
  passwordLogin: 'passwordLogin',
  otherLoginMethod: 'otherLoginMethod',
  passkey: 'passkey',
} as const;

export const SSO_UPDATE_MOBILE_NUMBER_ACTION = {
  updateMobileNumber: 'updateMobileNumber',
  verifyMobileNumber: 'verifyMobileNumber',
} as const;

export const SSO_FORGOT_PASSWORD_ACTION = {
  forgotPassword: 'forgotpassword',
  submit: 'submit',
} as const;

/** Error type segment of the SSO error category, e.g. `error|sso|validation`. */
export const SSO_ERROR_TYPE = {
  validation: 'validation',
  api: 'api',
} as const;

export type SsoErrorType = (typeof SSO_ERROR_TYPE)[keyof typeof SSO_ERROR_TYPE];

export const SSO_LOGIN_MODAL_ACTION = {
  signUpLink: 'signUpLink',
  switchLoginEmail: 'switchLoginEmail',
  switchLoginMobilePhone: 'switchLoginMobilePhone',
  passkey: 'passkey',
  socialLogin: 'socialLogin',
} as const;

export const SSO_SIGNUP_MODAL_ACTION = {
  signupButton: 'signupButton',
  loginLink: 'loginLink',
  socialLogin: 'socialLogin',
  userDetails: 'userDetails',
  submitUserDetails: 'submitUserDetails',
  emailOtpInput: 'EmailOTPInput',
  emailOtpDeeplink: 'EmailOTPDeeplink',
} as const;

export function buildSsoPipeLabel(
  ...parts: (string | null | undefined)[]
): string {
  return parts.filter((part): part is string => Boolean(part)).join('|');
}

export function buildSsoLoginModalLabel(
  method: SsoMethod,
  action?: string,
): string {
  return buildSsoPipeLabel(
    SSO_ANALYTICS_CONSUMER,
    'loginModal',
    method,
    action,
  );
}

export function buildSsoSignupModalLabel(
  method: SsoAuthMethod,
  action?: string,
): string {
  return buildSsoPipeLabel(
    SSO_ANALYTICS_CONSUMER,
    'signupModal',
    method,
    action,
  );
}

export function buildSsoSocialLabel(
  baseLabel: string,
  provider: string,
): string {
  return `${baseLabel}|${SSO_LOGIN_MODAL_ACTION.socialLogin}|${provider}`;
}

export function buildSsoAuthSuccessLabel(
  method: SsoAuthMethod,
  userId?: string | null,
): string {
  return buildSsoLabel(
    `${SSO_AUTH_SUCCESS_CONSUMER}|${method}`,
    userId ?? null,
  );
}

export function getSsoOtpLabels(
  verificationMethod: string,
  method: SsoAuthMethod,
) {
  return {
    screen: buildSsoPipeLabel(
      SSO_ANALYTICS_CONSUMER,
      verificationMethod,
      method,
    ),
    requestOTP: buildSsoPipeLabel(
      SSO_ANALYTICS_CONSUMER,
      SSO_OTP_ACTION.requestOTP,
      method,
    ),
    resendSMS: buildSsoPipeLabel(
      SSO_ANALYTICS_CONSUMER,
      SSO_OTP_ACTION.resendSMS,
      method,
    ),
    resendWhatsapp: buildSsoPipeLabel(
      SSO_ANALYTICS_CONSUMER,
      SSO_OTP_ACTION.resendWhatsapp,
      method,
    ),
    updatePhoneNumber: buildSsoPipeLabel(
      SSO_ANALYTICS_CONSUMER,
      SSO_OTP_ACTION.updatePhoneNumber,
      method,
    ),
    option: (action: string) =>
      buildSsoPipeLabel(SSO_ANALYTICS_CONSUMER, action, method),
  };
}

/** Labels for the "Enter new mobile number" screen during login / signup. */
export function getSsoUpdateMobileNumberLabels(method: SsoAuthMethod) {
  const screen = buildSsoPipeLabel(
    SSO_ANALYTICS_CONSUMER,
    SSO_UPDATE_MOBILE_NUMBER_ACTION.updateMobileNumber,
    method,
  );
  return {
    screen,
    verifyMobileNumber: buildSsoPipeLabel(
      screen,
      SSO_UPDATE_MOBILE_NUMBER_ACTION.verifyMobileNumber,
    ),
  };
}

export function getSsoAuthSuccessAnalytics(isSignup: boolean) {
  return {
    event: isSignup
      ? EVENT_NAMES.ssoSignupSuccessful
      : EVENT_NAMES.ssoLoginSuccessful,
    category: isSignup ? CATEGORY.ssoSignup : CATEGORY.ssoLogin,
    screenName: SCREEN_NAME.homeV2,
  };
}

export function resolveSsoEmailMethod(isPasswordless: boolean): SsoMethod {
  return isPasswordless ? SSO_METHOD.emailOnly : SSO_METHOD.emailPassword;
}

export function resolveSsoLoginMethod({
  isMobileLogin,
  isPasswordless,
}: {
  isMobileLogin: boolean;
  isPasswordless: boolean;
}): SsoMethod {
  if (isMobileLogin) return SSO_METHOD.mobilePhoneLogin;
  return resolveSsoEmailMethod(isPasswordless);
}

export function getSsoLoginModalLabels(method: SsoMethod) {
  const screen = buildSsoLoginModalLabel(method);
  return {
    screen,
    signUpLink: buildSsoLoginModalLabel(
      method,
      SSO_LOGIN_MODAL_ACTION.signUpLink,
    ),
    switchLoginEmail: buildSsoLoginModalLabel(
      method,
      SSO_LOGIN_MODAL_ACTION.switchLoginEmail,
    ),
    switchLoginMobilePhone: buildSsoLoginModalLabel(
      method,
      SSO_LOGIN_MODAL_ACTION.switchLoginMobilePhone,
    ),
    passkey: buildSsoLoginModalLabel(method, SSO_LOGIN_MODAL_ACTION.passkey),
    socialLogin: (provider: string) => buildSsoSocialLabel(screen, provider),
  };
}

export function getSsoSignupModalLabels(method: SsoAuthMethod) {
  const screen = buildSsoSignupModalLabel(method);
  return {
    screen,
    signupButton: buildSsoSignupModalLabel(
      method,
      SSO_SIGNUP_MODAL_ACTION.signupButton,
    ),
    loginLink: buildSsoSignupModalLabel(
      method,
      SSO_SIGNUP_MODAL_ACTION.loginLink,
    ),
    userDetails: buildSsoSignupModalLabel(
      method,
      SSO_SIGNUP_MODAL_ACTION.userDetails,
    ),
    submitUserDetails: buildSsoSignupModalLabel(
      method,
      SSO_SIGNUP_MODAL_ACTION.submitUserDetails,
    ),
    emailOtpInput: buildSsoSignupModalLabel(
      method,
      SSO_SIGNUP_MODAL_ACTION.emailOtpInput,
    ),
    socialLogin: (provider: string) => buildSsoSocialLabel(screen, provider),
  };
}

export function getSsoForgotPasswordLabels() {
  const screen = buildSsoPipeLabel(SSO_ANALYTICS_CONSUMER, 'loginModal');
  return {
    screen,
    forgotPassword: buildSsoPipeLabel(
      screen,
      SSO_FORGOT_PASSWORD_ACTION.forgotPassword,
    ),
    submit: buildSsoPipeLabel(screen, SSO_FORGOT_PASSWORD_ACTION.submit),
  };
}

export type AnalyticsLogEvent = (
  event: string,
  params: ReturnType<typeof buildAnalyticsParam> & { sso_id?: string },
) => void;

/** Binds `logEvent` to a screen + category so callers only pass event + label. */
export function createAnalyticsLogger(
  logEvent: AnalyticsLogEvent,
  screenName: string,
  category: string,
  enabled = true,
) {
  return (event: string, label: string) => {
    if (!enabled) return;
    logEvent(event, buildAnalyticsParam(screenName, category, label));
  };
}

export function logSsoAuthSuccess(
  logEvent: AnalyticsLogEvent,
  isSignup: boolean,
  method: SsoAuthMethod,
  userId?: string | null,
) {
  const success = getSsoAuthSuccessAnalytics(isSignup);
  logEvent(success.event, {
    ...buildAnalyticsParam(
      success.screenName,
      success.category,
      buildSsoAuthSuccessLabel(method, userId),
    ),
    ...(userId ? { sso_id: userId } : {}),
  });
}

/** How the email was activated: OTP entered or email link. */
export type SignupActivationSource =
  | typeof SSO_SIGNUP_MODAL_ACTION.emailOtpInput
  | typeof SSO_SIGNUP_MODAL_ACTION.emailOtpDeeplink;

/**
 * Logs `signup_activation_successful` for a new account, e.g.
 * `homepage|signupModal|emailOnly|EmailOTPInput`.
 * Use `EmailOTPDeeplink` when activated from the email link.
 */
export function logSignupEmailOtpActivation(
  logEvent: AnalyticsLogEvent,
  method: SsoAuthMethod = SSO_METHOD.emailOnly,
  source: SignupActivationSource = SSO_SIGNUP_MODAL_ACTION.emailOtpInput,
) {
  createAnalyticsLogger(
    logEvent,
    SCREEN_NAME.accountActivationEmailOTP,
    CATEGORY.ssoSignup,
  )(
    EVENT_NAMES.signupActivationSuccessful,
    buildSsoPipeLabel(SSO_AUTH_SUCCESS_CONSUMER, 'signupModal', method, source),
  );
}

export function buildSsoErrorCategory(type: SsoErrorType | string): string {
  return `${CATEGORY.ssoError}|${type}`;
}

export function buildSsoErrorLabel(
  code: string | number | null | undefined,
  description: string | null | undefined,
  message?: string | null,
): string {
  return buildSsoPipeLabel(
    code == null ? null : String(code),
    description,
    message,
  );
}

export function logSsoError(
  logEvent: AnalyticsLogEvent,
  {
    screenName,
    type,
    code,
    description,
    message,
  }: {
    screenName: string;
    type: SsoErrorType | string;
    code?: string | number | null;
    description?: string | null;
    message?: string | null;
  },
) {
  createAnalyticsLogger(
    logEvent,
    screenName,
    buildSsoErrorCategory(type),
  )(EVENT_NAMES.errors, buildSsoErrorLabel(code, description, message));
}
